import { useState } from "react";
import { Award, Users, Trophy, ChevronDown, ChevronUp } from "lucide-react";
import { format, parse } from "date-fns";
import { ActiveBonusDTO, BonusType } from "@/types/bonus";
import { Panel, PanelRow } from "@/components/brand/Panel";

interface BonusProgramsSectionProps {
  programs: ActiveBonusDTO[];
}

const BonusProgramsSection = ({ programs }: BonusProgramsSectionProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (programs.length === 0) return null;

  return (
    <Panel
      icon={Award}
      title="Active Bonus Programs"
      action={
        <button
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="text-muted-foreground transition-colors hover:text-foreground"
          aria-label={
            isCollapsed ? "Expand bonus programs" : "Collapse bonus programs"
          }
        >
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronUp className="h-5 w-5" />
          )}
        </button>
      }
    >
      {!isCollapsed &&
        programs.map((program) => (
          <BonusProgramRow key={program.id} program={program} />
        ))}
    </Panel>
  );
};

/**
 * The three types say different things, deliberately: an INCENTIVE names no
 * dollar amount because it is awarded and paid outside the app, and surfacing
 * one here would promise an employee a number nobody committed to.
 *
 * The wording is left exactly as it was. This was a restyle, and a restyle
 * that quietly rewrites what a screen tells someone about their own money is
 * not a restyle.
 */
function BonusProgramRow({ program }: { program: ActiveBonusDTO }) {
  const period = `${format(parse(program.startDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")} – ${format(parse(program.endDate, "yyyy-MM-dd", new Date()), "MM/dd/yyyy")}`;

  if (program.bonusType === BonusType.SEASONAL) {
    const completed = program.userCompletedParties ?? 0;
    const required = program.requiredParties ?? 0;
    const progress = Math.min(program.progressPercent ?? 0, 100);

    return (
      <PanelRow>
        <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Award className="h-4 w-4 text-primary" />
          <h3 className="font-medium text-lg">{program.title}</h3>
          {/* On the title's line rather than below it. It is the least
              important thing in the row and used to cost it a whole line. */}
          <p className="ml-auto text-xs tabular-nums text-muted-foreground">
            Period: {period}
          </p>
        </div>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            Progress: {completed} / {required} parties
          </p>
          {program.dollarAmount != null && (
            <p className="font-medium">Bonus Amount: ${program.dollarAmount}</p>
          )}
          {/* The fill was bg-card — the same colour as the panel behind it —
              so the bar reported progress nobody could see at any value. The
              track was bg-muted, which on this palette is barely a shade off
              the card, so at 0% the row showed a gap where a bar should be. */}
          <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-foreground/10">
            <div
              className="h-2.5 rounded-full bg-primary"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            {Math.round(progress)}% complete
          </p>
          {program.notes && (
            <p className="text-xs italic text-muted-foreground">
              {program.notes}
            </p>
          )}
        </div>
      </PanelRow>
    );
  }

  if (program.bonusType === BonusType.REFERRAL) {
    return (
      <PanelRow>
        <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="font-medium text-lg">{program.title}</h3>
          {/* On the title's line rather than below it. It is the least
              important thing in the row and used to cost it a whole line. */}
          <p className="ml-auto text-xs tabular-nums text-muted-foreground">
            Period: {period}
          </p>
        </div>
        <div className="space-y-1 text-sm text-muted-foreground">
          {program.requiredParties != null && (
            <p>
              Get a bonus after the referred employee works{" "}
              {program.requiredParties}{" "}
              {program.requiredParties === 1 ? "party" : "parties"}!
            </p>
          )}
          {program.dollarAmount != null && (
            <p className="font-medium">Your reward: ${program.dollarAmount}</p>
          )}
          {program.notes && (
            <p className="text-xs italic text-muted-foreground">
              {program.notes}
            </p>
          )}
        </div>
      </PanelRow>
    );
  }

  // INCENTIVE
  return (
    <PanelRow>
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
        <Trophy className="h-4 w-4 text-warning" />
        <h3 className="font-medium text-lg">{program.title}</h3>
        {/* On the title's line rather than below it. It is the least
            important thing in the row and used to cost it a whole line. */}
        <p className="ml-auto text-xs tabular-nums text-muted-foreground">
          Period: {period}
        </p>
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">
        {program.topN != null && <p>Top {program.topN} performers rewarded!</p>}
        {program.notes && (
          <p className="text-xs italic text-muted-foreground">
            {program.notes}
          </p>
        )}
      </div>
    </PanelRow>
  );
}

export default BonusProgramsSection;
